/**
 * ECU Local Recalculation Engine (Frontend / Netlify)
 */

const ceil = Math.ceil;
const max = Math.max;

const LBS_TO_KG = 0.45359;
const CFT_TO_CBM = 35.3146667;

/**
 * Computes the billing quantity for specific cargo dimensions.
 */
export function computeQuantity(code, weightKg, volumeCbm) {
  const w = weightKg;
  const v = volumeCbm;
  const wLbs = w / LBS_TO_KG;
  const vCft = v * CFT_TO_CBM; 

  switch (code?.trim().toUpperCase()) {
    // ── Weight-based ─────────────────────────────────────────────────────────
    case "/100 KG":       return ceil(w / 100);
    case "/1000 KG":      return ceil(w / 1000);
    case "/100LBS":       return w / (0.45359 * 100);
    case "/2000LBS":      return wLbs / 2000;
    case "/500 KG":       return ceil(w / 500);
    case "/KG":           return w;
    case "/LBS":          return wLbs;
    case "/TON":          return max(w / 1000, 1);
    case "100/LBS":       return (w / 0.4536) / 100;
    case "PER QUINTALE":  { const q = ceil(w / 100); return q < 5.55 ? max(q, 1) : q; }
    case "W":             return w / 1000;
    case "W/10":          return parseFloat((w / 1000).toFixed(2));

    // ── Volume-based ─────────────────────────────────────────────────────────
    case "/4 CBM":        return v / 4;
    case "/CFT":          return vCft;
    case "CBM":           return v;
    case "M":             return v;

    // ── Weight/Volume (W/M) ───────────────────────────────────────────────────
    case "1000 KGS/M2":   return max(ceil(w / 1000), ceil(v));
    case "1000 LBS/M":    return max(wLbs / 1000, v);
    case "100LBS/CFT":    return max(wLbs / 100, vCft);
    case "100M/KG":       return max(v * 300, w) / 100;
    case "10LBS/CFT":     return wLbs > vCft * 10 ? wLbs / 45.359 : v * 3.5314667;
    case "250 KG/M":      return max(w / 250, v);
    case "300 KG/M":      return max(w / 300, v);
    case "300KG/CBM":     return max(w, v * 300);
    case "330 KG/M":      return max(w / 330, v);
    case "330KG/CBM":     return max(w / 330, v);
    case "330KG/M":       return max(w / 330, v);
    case "333 KG/M":      return max(w / 333, v);
    case "333KG/CBM":     return max(w, v * 333);
    case "350 KG/M":      return max(w / 350, v);
    case "350KG/CBM":     return max(w, v * 350);
    case "363 KG/M":      return max(w / 363, v);
    case "3W/M":          return ceil(max(w / 1000, v) / 3);
    case "3CBM/100KG":    return max(ceil(w / 100), ceil(v * 3));
    case "400 KG/M":      return max(w / 400, v);
    case "400KG/M":       return max(w / 400, v);
    case "454 KG/M":      return max(w / 454, v);
    case "455 KG/M":      return max(w / 455, v);
    case "4W/M":          return ceil(max(w / 1000, v) / 4);
    case "500 KG/M":      return max(w / 500, v);
    case "500 KG/M2":     return max(ceil(w / 500), ceil(v));
    case "62LBS/CFT":     return max(wLbs / 62, vCft);
    case "667 KG/M":      return max(w / 667, v);
    case "700 KG/M":      return max(w / 700, v);
    case "7KG/CFT":       return max(w, (v / 0.0283685) * 7);
    case "800 LBS/M":     return max(wLbs / 800, v);
    case "CBM/800 LBS":   return max(v, (w / 0.4536) / 800);
    case "KG/120":        return max(w, v * 0.120);
    case "KG/233":        return max(w, v * 0.233333);
    case "PER QTL TAS":   return max(max(ceil(w / 100), ceil(v * 300 / 100)), 10);
    case "PER QTL TAS RND": return max(ceil(w / 100), ceil(v * 300 / 100));
    case "T/3M³":         return ceil(max(v / 3, w / 1000));
    case "TON/2.8CBM":    return max(w / 1000, v / 2.8);
    case "TON/2CBM":      return max(w / 1000, v / 2);
    case "TON/333":       return max(w / 1000, v * 0.333);
    case "TON/3M³":       return max(w / 1000, v / 3);
    case "TON/M2":        return max(max(w / 1000, v / 2), 1);
    case "W/2CBM":        return max(w / 1000, v / 2);
    case "W/M":           return max(w / 1000, v);
    case "W/M ALL-IN":    return max(w / 1000, v);
    case "W/M RND":       return Math.round(max(w / 1000, v) * 2) / 2;
    case "W/M2":          return max(ceil(w / 1000), ceil(v));
    case "WT/2CBM":       return max(max(w / 1000, v), 1);
    case "W/3M3":         return max(w, v / 3);

    // ── Flat / per-shipment ───────────────────────────────────────────────────
    case "%":
    case "/SHIPMENT":
    case "/BL":
    case "/DOCUMENT":
    case "/SET":
    case "/CON":
    case "LUMPSUM":
      return 1;

    default:
      return 1;
  }
}

/**
 * Calculates the exact charge in USD when a user changes Weight or Volume.
 * * @param {Object} storedCharge   - From DB (needs ratePerUnit, minimumUSD, calculationCode, unitCode)
 * @param {number} actualWeightKg - Metric Weight
 * @param {number} actualVolumeCbm- Metric Volume
 */
export function calculateLocalCharge(storedCharge, actualWeightKg, actualVolumeCbm) {
  //console.log("calculateLocalCharge...., storedCharge: ",storedCharge,"\n actualWeightKg: ",actualWeightKg,"\n actualVolumeCbm: ",actualVolumeCbm)
  const minimumUSD = storedCharge.minimumUSD || 0;
  
  // 1. Mirror ECU's formula perfectly
  if (storedCharge.calculationCode) {
    let exactQty = computeQuantity(storedCharge.calculationCode, actualWeightKg, actualVolumeCbm);
    //console.log("storedCharge.ratePerUnit: ",storedCharge.ratePerUnit, "exactQty: ",exactQty)
    if(storedCharge.calculationCode === "%"){
       exactQty = storedCharge?.quantity * actualVolumeCbm || exactQty
    }
    //console.log("exactQty: ",exactQty,"\n chargeAmount: ",max(storedCharge.ratePerUnit * exactQty, minimumUSD))
    return max(storedCharge.ratePerUnit * exactQty, minimumUSD);
  }

  // 2. Fallback logic if calculationCode is missing
  let qty = 1;
  if (storedCharge.unitCode === "KG") {
    qty = actualWeightKg;
  } else if (storedCharge.unitCode === "CBM") {
    qty = max(actualVolumeCbm, actualWeightKg / 1000);
  } 
  
  return max(storedCharge.ratePerUnit * qty, minimumUSD);
}

/**
 * Universal wrapper for your UI components. 
 * Allows users to input values in either Metric (KG/CBM) or Imperial (LBS/CFT).
 */
export function calculateChargeUniversal(storedCharge, weight, volume, isImperial = false) {
  let weightKg = weight;
  let volumeCbm = volume;

  if (isImperial) {
    weightKg = weight * LBS_TO_KG;
    volumeCbm = volume / CFT_TO_CBM;
  }

  return calculateLocalCharge(storedCharge, weightKg, volumeCbm);
}